import React from 'react';
import { Link } from 'react-router-dom';
import { Package, ShoppingBag, Factory, Truck, ArrowRight } from 'lucide-react';

const Industries = () => {
  const industries = [
    {
      icon: <ShoppingBag className="w-10 h-10" />,
      title: 'Retail Imports',
      description: 'Freight forwarding and customs clearance for South African retailers importing products from Europe and international suppliers. We coordinate container shipments, consolidation, and delivery to distribution centers.',
      needs: ['Regular ocean freight', 'Consolidated LCL shipments', 'Customs clearance', 'Distribution coordination']
    },
    {
      icon: <Factory className="w-10 h-10" />,
      title: 'Manufacturing & Industrial',
      description: 'Import coordination for manufacturers bringing in raw materials, components, and equipment from Europe and Africa. Time-sensitive air freight for critical parts and cost-effective ocean freight for bulk materials.',
      needs: ['Raw material imports', 'Equipment and machinery', 'Air freight for urgent parts', 'Export of finished goods']
    },
    {
      icon: <Package className="w-10 h-10" />,
      title: 'General Commercial Cargo',
      description: 'Flexible freight solutions for SMEs and businesses moving diverse commercial cargo between South Africa, Europe, and Africa. Tailored support for various cargo types and shipping requirements.',
      needs: ['Flexible freight options', 'Small to medium shipments', 'Import and export support', 'Complete documentation assistance']
    },
    {
      icon: <Truck className="w-10 h-10" />,
      title: 'Supply Chain Operations',
      description: 'Logistics coordination for businesses with ongoing import/export operations. Support for multi-origin sourcing, regular shipment scheduling, and supply chain visibility across the Europe–Africa corridor.',
      needs: ['Multi-shipment coordination', 'Regular freight schedules', 'Route optimization', 'Consolidated logistics management']
    }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="industries-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            <span className="text-xs font-mono tracking-widest uppercase text-orange-500">Industries Served</span>
            <h1 
              className="text-5xl md:text-7xl font-bold tracking-tight uppercase mt-4 mb-6"
              style={{fontFamily: 'Barlow Condensed, sans-serif'}}
            >
              Freight Solutions for Diverse Businesses
            </h1>
            <p className="text-xl leading-relaxed text-slate-600">
              NETEXEC GLOBAL serves South African businesses across multiple industries, providing tailored freight forwarding and customs clearance solutions for commercial cargo movement.
            </p>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="bg-slate-50 border border-slate-200 p-8 rounded-sm"
                data-testid={`industry-${index}`}
              >
                <div className="text-orange-500 mb-4">
                  {industry.icon}
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-slate-900">{industry.title}</h3>
                <p className="text-base leading-relaxed text-slate-600 mb-6">{industry.description}</p>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-900 uppercase tracking-wider">Typical Needs:</p>
                  <ul className="space-y-2">
                    {industry.needs.map((need, needIndex) => (
                      <li key={needIndex} className="text-sm text-slate-600 flex items-start gap-2">
                        <span className="text-orange-500 mt-1">•</span>
                        {need}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 
            className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-6"
            style={{fontFamily: 'Barlow Condensed, sans-serif'}}
          >
            Your Industry, Our Expertise
          </h2>
          <p className="text-lg leading-relaxed text-slate-300 mb-8">
            Whether you're importing retail goods, manufacturing inputs, or managing complex supply chains, NETEXEC GLOBAL provides tailored freight and clearance solutions.
          </p>
          <Link
            to="/contact"
            data-testid="industries-cta-btn"
            className="bg-orange-500 text-white px-8 py-4 rounded-sm text-base font-medium hover:bg-orange-600 transition-colors duration-300 inline-flex items-center gap-2"
          >
            Discuss Your Needs
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Industries;